import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiBell } from 'react-icons/fi';
import { api } from '../../../services/api';
import { RichContent } from '../../announcements/RichContent';
import { Modal } from '../../ui/Modal';
import styles from './NotificationBell.module.css';

type Notification = Awaited<ReturnType<typeof api.getNotifications>>[number];

function timeAgo(iso: string): string {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} h`;
    const days = Math.floor(h / 24);
    if (days < 30) return `${days} d`;
    return d.toLocaleDateString('pt-BR');
}

export function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);
    const [announcement, setAnnouncement] = useState<{ title: string; html: string | null; error: boolean } | null>(null);

    const unread = items.filter((n) => !n.isRead).length;

    const load = useCallback(() => {
        setLoading(true);
        api.getNotifications()
            .then(setItems)
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    // Carga inicial + polling leve para o badge
    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next) load();
    };

    const handleMarkAll = async () => {
        if (unread === 0) return;
        setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
        try {
            await api.markAllNotificationsRead();
        } catch {
            load();
        }
    };

    const handleClickItem = async (n: Notification) => {
        if (!n.isRead) {
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
            api.markNotificationRead(n.id).catch(() => { });
        }
        if (n.announcementId) {
            openAnnouncement(n);
            return;
        }
        if (n.link) window.open(n.link, '_blank', 'noopener,noreferrer');
    };

    // Busca a cada abertura: as URLs assinadas dos arquivos vencem.
    const openAnnouncement = async (n: Notification) => {
        setOpen(false);
        setAnnouncement({ title: n.title, html: null, error: false });
        try {
            const content = await api.getNotificationContent(n.id);
            setAnnouncement({ title: content.title, html: content.contentHtml, error: false });
        } catch {
            setAnnouncement({ title: n.title, html: null, error: true });
            load();
        }
    };

    return (
        <div className={styles.wrapper}>
            <button className={styles.bellBtn} onClick={handleToggle} title="Notificações">
                <FiBell size={20} />
                {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
            </button>

            {open && (
                <>
                    <div className={styles.backdrop} onClick={() => setOpen(false)} />
                    <div className={styles.dropdown}>
                        <div className={styles.dropdownHeader}>
                            <span>Notificações</span>
                            {unread > 0 && (
                                <button className={styles.markAll} onClick={handleMarkAll}>
                                    Marcar todas como lidas
                                </button>
                            )}
                        </div>

                        <div className={styles.list}>
                            {loading && items.length === 0 ? (
                                <p className={styles.empty}>Carregando…</p>
                            ) : items.length === 0 ? (
                                <p className={styles.empty}>Nenhuma notificação.</p>
                            ) : (
                                items.map((n) => (
                                    <button
                                        key={n.id}
                                        className={`${styles.item} ${n.isRead ? '' : styles.itemUnread}`}
                                        onClick={() => handleClickItem(n)}
                                    >
                                        <div className={styles.itemTop}>
                                            <span className={styles.itemTitle}>{n.title}</span>
                                            <span className={styles.itemTime}>{timeAgo(n.createdAt)}</span>
                                        </div>
                                        <p className={styles.itemBody}>{n.body}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}

            {announcement && createPortal(
                <Modal isOpen onClose={() => setAnnouncement(null)} title={announcement.title} size="xlarge" scrollBody>
                    {announcement.error ? (
                        <p className={styles.empty}>Este comunicado não está mais disponível.</p>
                    ) : announcement.html === null ? (
                        <p className={styles.empty}>Carregando…</p>
                    ) : (
                        <RichContent html={announcement.html} />
                    )}
                </Modal>,
                document.body,
            )}
        </div>
    );
}
